/**
 * Viability check — "Can we deliver our priorities?"
 *
 * The five viability inputs as a guided step rail (each step's status is
 * derived from the live run), the answer card, and every priority's outcome
 * in plain language. Steps are escape hatches into the one B-Mode tab that
 * edits that input — the app never replicates the editors.
 */

import { useNavigate } from 'react-router-dom';
import { ExternalLink, RefreshCw } from 'lucide-react';
import { FEASIBILITY_PLAIN_LANGUAGE, feasibilityTone } from '@/lib/planningFeasibilityLanguage';
import { appTelemetry } from '@/lib/appTelemetry';
import { getAppDefinition } from '../registry';
import { AppShell, BallLine, ScreenHeading } from '../components/AppShell';
import { AppCard, EmptyState, Eyebrow, SecondaryButton, StatusChip } from '../components/primitives';
import { StepRail, type FlowStep } from '../components/flow';
import { useViabilityRun } from './useViabilityData';
import { ViabilityScenarioChoice } from './ViabilityScenarioChoice';

const TONE_DOT: Record<string, string> = {
  green: 'bg-emerald-500',
  amber: 'bg-amber-500',
  red: 'bg-red-500',
  neutral: 'bg-gray-300 dark:bg-[var(--surface-raised)]',
};

export default function ViabilityCheckScreen() {
  const navigate = useNavigate();
  const app = getAppDefinition('viability')!;
  const {
    projectId,
    scenario,
    scenarios,
    usingLiveItemsFallback,
    output,
    outcomes,
    running,
    error,
    capacityRowCount,
    rerun,
    chooseScenario,
    chooseError,
  } = useViabilityRun();

  const exec = output?.executiveSummary ?? null;

  const steps: FlowStep[] = [
    {
      key: 'scenarios',
      label: 'Scenario',
      detail: scenario
        ? `“${scenario.name}” — the assumptions this check runs on`
        : 'Using live-items defaults — create a scenario to pin assumptions',
      status: scenario ? 'done' : usingLiveItemsFallback ? 'warn' : 'todo',
    },
    {
      key: 'priorities',
      label: 'Priorities',
      detail:
        outcomes.length > 0
          ? `${outcomes.length} in scope`
          : 'Nothing in scope yet — add or sync priorities',
      status: outcomes.length > 0 ? 'done' : 'warn',
    },
    {
      key: 'work-units',
      label: 'Sizing',
      detail:
        exec && exec.needsSizingCount > 0
          ? `${exec.needsSizingCount} still need a size`
          : 'Everything in scope has a size',
      status: exec ? (exec.needsSizingCount > 0 ? 'warn' : 'done') : 'todo',
    },
    {
      key: 'dependencies',
      label: 'Dependencies',
      detail: 'Optional — record hard blockers so ordering is honest',
      status: 'done',
    },
    {
      key: 'capacity',
      label: 'Resource budget',
      detail:
        capacityRowCount === 0 || (exec?.noCapacityModelCount ?? 0) > 0
          ? 'No capacity model yet — the check can only count demand'
          : 'Capacity model in place',
      status:
        capacityRowCount === 0 || (exec?.noCapacityModelCount ?? 0) > 0
          ? 'warn'
          : capacityRowCount == null
            ? 'todo'
            : 'done',
    },
  ];

  return (
    <AppShell app={app} backTo="/apps/viability" backLabel="Back to Viability">
      <ScreenHeading
        title="Can we deliver our priorities?"
        subtitle="Five inputs, one deterministic answer. Fix a step, run again."
      />
      <BallLine ballWith={running ? 'the engine — computing' : 'you — review the answer'} />

      <div className="space-y-4">
        <ViabilityScenarioChoice
          scenario={scenario}
          scenarios={scenarios}
          onChoose={(id) => void chooseScenario(id)}
          disabled={running}
          error={chooseError}
        />

        <AppCard>
          <div className="flex items-center justify-between">
            <Eyebrow>The answer</Eyebrow>
            <SecondaryButton onClick={rerun} disabled={running} className="!py-1.5 !px-3">
              <RefreshCw className={`h-3.5 w-3.5 ${running ? 'animate-spin' : ''}`} />
              {running ? 'Running…' : 'Run again'}
            </SecondaryButton>
          </div>
          {error ? (
            <p className="mt-3 font-body text-sm text-luminous-coral-700 dark:text-luminous-coral-300">
              {error}
            </p>
          ) : exec ? (
            <>
              <p className="mt-3 font-heading text-xl font-semibold text-gray-900 dark:text-[var(--text-pri)]">
                {FEASIBILITY_PLAIN_LANGUAGE[exec.overallFeasibility]}
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <StatusChip label={`${exec.includedCount} fit`} tone="green" />
                {exec.deferredCount > 0 ? (
                  <StatusChip label={`${exec.deferredCount} don't fit`} tone="coral" />
                ) : null}
                {exec.needsSizingCount > 0 ? (
                  <StatusChip label={`${exec.needsSizingCount} need sizing`} tone="mustard" />
                ) : null}
                {exec.participationAtRiskCount > 0 ? (
                  <StatusChip label={`${exec.participationAtRiskCount} at risk — participation`} tone="mustard" />
                ) : null}
              </div>
            </>
          ) : (
            <p className="mt-3 font-body text-sm text-gray-500 dark:text-[var(--text-ter)]">
              {projectId
                ? 'Computing the first answer…'
                : 'Waiting for your workspace to load — the check runs as soon as it does.'}
            </p>
          )}
        </AppCard>

        <div>
          <div className="mb-2">
            <Eyebrow>The five inputs</Eyebrow>
          </div>
          <StepRail
            steps={steps}
            onSelect={(key) => navigate(`/planning/engine/${key}`)}
          />
          <p className="mt-2 font-body text-xs text-gray-500 dark:text-[var(--text-ter)]">
            Each step opens the full editor for that input, then come back and
            run again.
          </p>
        </div>

        {outcomes.length > 0 ? (
          <AppCard className="!p-0">
            <div className="px-5 pt-5">
              <Eyebrow>Every priority, plainly</Eyebrow>
            </div>
            <div className="mt-2">
              {outcomes.map((o) => {
                const tone = o.summary.hasParticipationRisk
                  ? 'amber'
                  : feasibilityTone(o.summary.feasibility);
                return (
                  <div
                    key={o.summary.priority.id}
                    className="flex items-center gap-3 border-b border-gray-100 px-5 py-3 last:border-0 dark:border-[var(--rule)]"
                  >
                    <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${TONE_DOT[tone]}`} />
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-body text-sm font-medium text-gray-900 dark:text-[var(--text-pri)]">
                        {o.summary.priority.name}
                      </p>
                      <p className="truncate font-body text-xs text-gray-500 dark:text-[var(--text-ter)]">
                        {o.plainReason}
                      </p>
                    </div>
                    <StatusChip
                      label={FEASIBILITY_PLAIN_LANGUAGE[o.summary.feasibility]}
                      tone={tone === 'green' ? 'green' : tone === 'red' ? 'coral' : tone === 'amber' ? 'mustard' : 'neutral'}
                    />
                  </div>
                );
              })}
            </div>
          </AppCard>
        ) : !running && exec ? (
          <EmptyState
            title="Nothing in scope yet"
            hint="Add priorities (or capture items in the Items app) and the check will pick them up."
          />
        ) : null}

        <div className="flex justify-end">
          <SecondaryButton
            onClick={() => {
              // FR-20: the escape hatch was taken — App -> full platform.
              void appTelemetry.promoted('viability', '/planning/engine');
              navigate('/planning/engine');
            }}
          >
            <ExternalLink className="h-4 w-4" /> Open the full workbench
          </SecondaryButton>
        </div>
      </div>
    </AppShell>
  );
}
